import { Loader2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';

import { useAuth } from '@/contexts/auth.context';
import { useUserSync } from '@/features/auth/hooks/useUserSync';
import AuthenticationPage from '@/features/auth/pages/AuthenticationPage';

import ApplicationsPage from './applications/pages/ApplicationsPage';
import ApplyLandingPage from './apply/pages/ApplyLandingPage';
import CVUploadPage from './apply/pages/CVUploadPage';
import InterviewStartPage from './apply/pages/InterviewStartPage';
import DashboardPage from './cv/pages/CVListPage';
import JobListPage from './jobs/pages/JobListPage';
import ProfilePage from './profile/pages/ProfilePage';

/** Paths where we must not show the global loading overlay so that local state (e.g. OTP step) is not lost when session refetches on tab focus. */
const PATHS_SKIP_LOADING: readonly string[] = ['/auth', '/apply'];

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (import.meta.env.DEV) {
    console.log('[ProtectedRoute]', { isAuthenticated, isLoading, pathname: location.pathname });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="size-10 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?next=${next}`} replace />;
  }

  return <>{children}</>;
}

function DashboardRoutes() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Sync authenticated user to UserContext
  useUserSync();

  const skipLoading = useMemo(
    () =>
      PATHS_SKIP_LOADING.some(
        p => location.pathname === p || location.pathname.startsWith(p + '/'),
      ),
    [location.pathname],
  );

  if (isLoading && !skipLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="size-10 animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/auth"
        element={isAuthenticated ? <Navigate to="/" replace /> : <AuthenticationPage />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs"
        element={
          <ProtectedRoute>
            <JobListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/applications"
        element={
          <ProtectedRoute>
            <ApplicationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apply/:jobId"
        element={
          <ProtectedRoute>
            <ApplyLandingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apply/:jobId/upload"
        element={
          <ProtectedRoute>
            <CVUploadPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apply/:jobId/interview"
        element={
          <ProtectedRoute>
            <InterviewStartPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="*"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Navigate to="/auth" replace />}
      />
    </Routes>
  );
}

export default function DashboardRouter() {
  // Add dashboard-mode class to body to allow text selection
  useEffect(() => {
    document.body.classList.add('dashboard-mode');
    return () => {
      document.body.classList.remove('dashboard-mode');
    };
  }, []);

  return <DashboardRoutes />;
}
